import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { textVariants } from '@/ui/Text';
import { cn } from '@/utils';

const alertClass = textVariants({
	variant: 'description',
	className: cn(
		'relative w-full rounded-lg border p-3 pt-4 [&>svg~*]:pl-9 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-2'
	),
});

const alertVariants = cva(alertClass, {
	variants: {
		variant: {
			default: 'bg-brand-blue/5 text-brand-blue border-brand-blue',
			error: 'bg-brand-red/5 text-brand-red border-brand-red',
			success: 'bg-brand-green/5 text-brand-green border-brand-green',
		},
	},
	defaultVariants: {
		variant: 'default',
	},
});

const Alert = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
	<div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...props} />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
	({ className, children, ...props }, ref) => (
		<h5 ref={ref} className={cn('mb-1 font-medium leading-none tracking-tight', className)} {...props}>
			{children}
		</h5>
	)
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
	({ className, ...props }, ref) => (
		<div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...props} />
	)
);
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertDescription, AlertTitle };
